import * as React from "react";

// Shared testimonial UI (gold stars, Google rating badge, review cards).
// WorkWithChris.js currently keeps its own page-scoped copy of these styles
// and data; when it migrates here, update the numbers in one place only.
//
// MGDWD-254: all Trustpilot branding (name, green stars, score badge,
// "Verified" mark) was removed after Trustpilot's brand-misuse notice — we
// are on their free plan, which does not allow showing the score or reviews
// outside their widgets. The quotes themselves stay as plain customer
// testimonials.

// Google store rating (Google Seller Ratings, aggregated from our Trustpilot
// reviews — see MGDWD-255). Public page, verified 2026-08-21. Update the
// numbers manually when the rating moves.
export const GOOGLE_RATING_URL =
	"https://www.google.com/shopping/ratings/account/metrics?q=mygolfdna.com&c=US&v=19";
export const GOOGLE_SCORE = "5.0";
export const GOOGLE_COUNT = "88";

// Our own customer testimonials, taken from what students wrote to Chris
// (same set as /work-with-chris). MGDWD-269: the previous six were quotes
// from Trustpilot, which its free plan does not allow us to display —
// their cease-and-desist named the review text itself, not just branding.
export const testimonials = [
	{
		name: "Mike Mitchell",
		initials: "MM",
		color: "#fce9c8",
		quote: "I played a round of golf this morning and couldn't believe the distance I was getting with my clubs. It was a 60 degree day, slight wind that was helping, 155y par 3. I hit the purest 8i I've ever hit, it flew over the green and rolled out to 185y. So now I have a good problem of figuring out how far all my clubs will go, everything seems to go about 20-30 yards further than it used to and it's all thanks to you!",
	},
	{
		name: "Lee F.",
		initials: "LF",
		color: "#f7d0c4",
		quote: "I practice this drill for a couple hours hitting off my divot board, no balls. Went to the range and put it all together. OMG the compression was amazing. I'm 60 years old and hitting my 52-degree 100 yards. That's awesome for me. So this is the best part, I tried it with my 3 wood, my favorite club in my bag. When I'm hitting it well before this drill, it would carry 170 yards. Now, with your drill, I swing easier, less effort, and carried the ball 252 yards, absolutely crushing it with no effort.",
	},
	{
		name: "Nick Bastiao",
		initials: "NB",
		color: "#d4e7f6",
		quote: "The ball striking is flipping incredible. The speed and trajectory of my ball flight is awesome! I can't believe how comfortable I feel! I feel so natural with this move. Best of all, ZERO BODY PAIN! Chris, I feel like you have awakened a caged animal in my golf life now. I'm so excited.",
	},
	{
		name: "Tom N.",
		initials: "TN",
		color: "#cfe8d9",
		quote: "I'm 76. If only we had your instruction 50+ years ago! Would have saved: a TON of money on golf lessons. A TON of time at the driving range pounding out buckets (and buckets) of balls. A TON of frustration trying to make bad swing habits into a good golf game.",
	},
	{
		name: "Curt Anderson",
		initials: "CA",
		color: "#e2d8f3",
		quote: "What a great initial lesson! This is exactly the type of feedback I am looking for. Really enjoy your style of teaching as you make no bones about it: making neurological firing adjustments (i.e., a \"swing change\") is not easy and can be tedious. Your acknowledgment of how difficult it is, and the need to teach your nervous system what to do through numerous repetitions, made me choose you as the best for me. Your core audience is people like me who live for that kind of teaching.",
	},
	{
		name: "Reynaldo M.",
		initials: "RM",
		color: "#f6d6e4",
		quote: "You are the best teacher I've ever had and I'd like to thank you for all your hard work on my behalf. When I reviewed my initial swing submissions to my most recent ones, it's incredible the difference even my untrained eye can see.",
	},
];

// Gold star colour — the same one Google uses for its ratings.
export const STAR_COLOR = "#fbbc04";

const styles = `
	.rv-stars {
		display: inline-flex;
		gap: 1px;
		color: ${STAR_COLOR};
		font-size: var(--rv-size, 18px);
		line-height: 1;
	}
	.rv-badge {
		display: inline-flex;
		align-items: center;
		gap: 10px;
		text-decoration: none;
		color: #212529;
	}
	.rv-badge.rv-badge-dark {
		color: rgba(255, 255, 255, 0.85);
	}
	.rv-badge .rv-badge-text b {
		font-weight: 700;
	}
	.rv-badge:hover .rv-badge-text {
		text-decoration: underline;
	}
	.rv-card {
		background: #fff;
		border: 1px solid #e7e9ee;
		border-radius: 12px;
		padding: 24px;
		box-shadow: 0 1px 2px rgba(16, 23, 31, 0.04);
	}
	.rv-card .rv-avatar {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		font-weight: 700;
		font-size: 0.85rem;
		color: #5b4636;
		flex-shrink: 0;
	}
	.rv-card .rv-quote {
		font-size: 0.93rem;
		color: #3d434b;
	}
`;

export function TestimonialStyles() {
	return <style>{styles}</style>;
}

export function Stars({size = 18}) {
	return (
		<span className="rv-stars" style={{"--rv-size": `${size}px`}} aria-label="5 out of 5 stars">
			{[0, 1, 2, 3, 4].map((i) => (
				<i key={i} className="bi bi-star-fill" />
			))}
		</span>
	);
}

// Google star rating badge — social-proof row in the hero and above the
// testimonial cards.
export function GoogleBadge({dark}) {
	return (
		<>
			<TestimonialStyles />
			<a
				className={`rv-badge ${dark ? "rv-badge-dark" : ""}`}
				href={GOOGLE_RATING_URL}
				target="_blank"
				rel="noopener noreferrer"
			>
				<Stars size={17} />
				<span className="rv-badge-text">
					<b>{GOOGLE_SCORE}</b> on <b>Google</b> · {GOOGLE_COUNT} reviews
				</span>
			</a>
		</>
	);
}

// Expects TestimonialStyles (or GoogleBadge) to be rendered on the page.
export function TestimonialCard({quote, name, initials, color}) {
	return (
		<div className="rv-card h-100">
			<div className="d-flex align-items-center gap-2 mb-2">
				<span className="rv-avatar" style={{backgroundColor: color}}>{initials}</span>
				<span className="fw-semibold">{name}</span>
			</div>
			<div className="d-flex align-items-center gap-2 mb-3">
				<Stars size={18} />
			</div>
			<p className="rv-quote mb-0">{quote}</p>
		</div>
	);
}
